import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Checkbox } from './ui/checkbox';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from './ui/tooltip';
import { Plus, ChevronLeft, Layers, GitMerge, Merge, Loader2, Check, X } from 'lucide-react';

export default function CriarLoteForm({
  categoriasFiltradas = [],
  operacoesPorCategoria = {},
  lotes = [],
  resumo = {},
  quantidadeTotal = 0,
  saving = false,
  setSaving,
  onClose,
  onSuccess,
  createLoteContext,
  unirLotesContext,
  ofId = '',
}) {
  const [novoLotePasso, setNovoLotePasso] = useState(1);
  const [novoLoteCategoria, setNovoLoteCategoria] = useState('');
  const [novoLoteTipo, setNovoLoteTipo] = useState('');
  const [novoLoteQuantidade, setNovoLoteQuantidade] = useState('');
  const [novoLoteObservacoes, setNovoLoteObservacoes] = useState('');
  const [novoLoteNome, setNovoLoteNome] = useState('');
  const [lotesParaUniao, setLotesParaUniao] = useState([]);
  const [categoriaOrigemUniao, setCategoriaOrigemUniao] = useState('');

  const lotesParaUniaoCategoriaFiltrados = useMemo(() => {
    if (!categoriaOrigemUniao) return [];
    return lotes.filter(l => 
      l.status === 'aprovado' &&
      l.categoria_id === categoriaOrigemUniao &&
      l.quantidade > 0
    );
  }, [categoriaOrigemUniao, lotes]);

  const resetNovoLoteForm = () => {
    setNovoLotePasso(1);
    setNovoLoteCategoria('');
    setNovoLoteTipo('');
    setNovoLoteQuantidade('');
    setNovoLoteObservacoes('');
    setNovoLoteNome('');
    setLotesParaUniao([]);
    setCategoriaOrigemUniao('');
  };

  // Função para abrir diálogo de alertar operadores

  // Obter lotes disponíveis para união (de outras categorias, pendentes e aprovados)
  // Permite planeamento antecipado mesmo antes de aprovar os lotes de origem
  const getLotesDisponiveis = () => {
    if (!categoriaOrigemUniao) return [];
    return lotes.filter(l => 
      (l.status === 'aprovado' || l.status === 'pendente') && 
      l.categoria_id === categoriaOrigemUniao &&
      l.quantidade > 0
    );
  };

  // Calcular total de peças selecionadas para união
  const getTotalPecasUniao = () => {
    return lotesParaUniao.reduce((total, item) => total + (parseInt(item.quantidade) || 0), 0);
  };

  // Toggle lote na selecção de união
  const toggleLoteUniao = (lote) => {
    const existe = lotesParaUniao.find(l => l.loteId === lote.id);
    if (existe) {
      setLotesParaUniao(prev => prev.filter(l => l.loteId !== lote.id));
    } else {
      setLotesParaUniao(prev => [...prev, { 
        loteId: lote.id, 
        codigo: lote.codigo_lote || `L${lote.numero_lote}`,
        maxQuantidade: lote.quantidade,
        quantidade: lote.quantidade.toString()
      }]);
    }
  };

  // Actualizar quantidade de um lote na união
  const updateQuantidadeUniao = (loteId, quantidade) => {
    setLotesParaUniao(prev => prev.map(l => 
      l.loteId === loteId ? { ...l, quantidade } : l
    ));
  };


  const handleCriarNovoLote = async () => {
    if (novoLoteTipo === 'novo') {
      // Criar lote novo
      const quantidade = parseInt(novoLoteQuantidade);
      if (!quantidade || quantidade <= 0) {
        toast.error('Insira uma quantidade válida');
        return;
      }
      
      // Calcular quantidade restante para a categoria seleccionada
      const distribuicao = resumo.distribuicao_categorias || {};
      const infoCat = distribuicao[novoLoteCategoria] || {};
      const quantidadeRestanteCategoria = infoCat.quantidade_restante !== undefined 
        ? infoCat.quantidade_restante 
        : (resumo.quantidade_total || quantidadeTotal);
      
      if (quantidade > quantidadeRestanteCategoria) {
        toast.error(`Quantidade excede o disponível nesta categoria (${quantidadeRestanteCategoria} peças restantes)`);
        return;
      }

      setSaving(true);
      try {
        await createLoteContext(ofId, {
          quantidade,
          observacoes: novoLoteObservacoes || null,
          categoria_id: novoLoteCategoria,
          nome_lote: novoLoteNome || null
        });
        resetNovoLoteForm();
        onSuccess && onSuccess();
      } catch (error) {
        // Recarregar dados para evitar resumo stale
        onSuccess && onSuccess();
      } finally {
        setSaving(false);
      }
    } else if (novoLoteTipo === 'uniao') {
      // Criar lote por união
      if (lotesParaUniao.length === 0) {
        toast.error('Selecione pelo menos um lote para unir');
        return;
      }

      const totalPecas = getTotalPecasUniao();
      if (totalPecas <= 0) {
        toast.error('O total de peças deve ser maior que zero');
        return;
      }

      // Validar que as quantidades não excedem o máximo de cada lote
      for (const item of lotesParaUniao) {
        const qtd = parseInt(item.quantidade) || 0;
        if (qtd > item.maxQuantidade) {
          toast.error(`Quantidade para ${item.codigo} excede o disponível (${item.maxQuantidade})`);
          return;
        }
        if (qtd <= 0) {
          toast.error(`Quantidade para ${item.codigo} deve ser maior que zero`);
          return;
        }
      }

      setSaving(true);
      try {
        await unirLotesContext(
          ofId, 
          lotesParaUniao.map(l => l.loteId),
          lotesParaUniao.map(l => parseInt(l.quantidade)),
          novoLoteCategoria,
          novoLoteObservacoes || ''
        );
                resetNovoLoteForm();
        onSuccess && onSuccess();
      } catch (error) {
        // Toast já mostrado pelo contexto
      } finally {
        setSaving(false);
      }
    }
  };



  return (
    <Card className="p-6 border-2 border-navy/30 bg-navy/5">
          <h4 className="font-bold text-lg mb-4 flex items-center gap-2 text-navy">
            <Plus className="h-5 w-5" />
            Criar Novo Lote
            {novoLotePasso > 1 && (
              <span className="text-sm font-normal text-stone-500 ml-2">
                Passo {novoLotePasso} de 4
              </span>
            )}
          </h4>

          {/* Passo 1: Seleccionar Categoria de Destino */}
          {novoLotePasso === 1 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-stone-600">Primeiro, seleccione a categoria para o novo lote:</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { onClose && onClose(); resetNovoLoteForm(); }}
                  className="text-stone-500 hover:text-stone-700"
                  data-testid="btn-cancelar-criar-lote"
                >
                  <X className="h-4 w-4 mr-1" />
                  Cancelar
                </Button>
              </div>
              <TooltipProvider delayDuration={300}>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {categoriasFiltradas.map(cat => {
                    const opsNaCategoria = operacoesPorCategoria[cat.id] || [];
                    const temOperacoes = opsNaCategoria.length > 0;
                    
                    return (
                      <Tooltip key={cat.id}>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => {
                              setNovoLoteCategoria(cat.id);
                              setNovoLotePasso(2);
                            }}
                            className={`p-4 rounded-lg border-2 transition-all hover:shadow-md relative ${
                              novoLoteCategoria === cat.id 
                                ? 'border-navy bg-navy/10' 
                                : temOperacoes
                                  ? 'border-green-400 bg-green-50 hover:border-green-500'
                                  : 'border-stone-200 hover:border-navy/50'
                            } ${cat.especial ? 'border-purple-300 bg-purple-50' : ''}`}
                            data-testid={`btn-categoria-${cat.codigo || cat.id}`}
                          >
                            <span className="block font-mono text-sm font-bold text-navy">{cat.codigo || '---'}</span>
                            <span className="block text-stone-700">{cat.nome}</span>
                            {cat.especial && <span className="text-xs text-purple-600 mt-1">Especial</span>}
                            {temOperacoes && (
                              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                {opsNaCategoria.length}
                              </span>
                            )}
                          </button>
                        </TooltipTrigger>
                        {temOperacoes && (
                          <TooltipContent side="bottom" className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-semibold text-sm">Operações nesta categoria:</p>
                              <ul className="text-xs space-y-0.5">
                                {opsNaCategoria.slice(0, 8).map((op, idx) => (
                                  <li key={op.id || idx} className="flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                    {op.nome || op.codigo || 'Operação'}
                                  </li>
                                ))}
                                {opsNaCategoria.length > 8 && (
                                  <li className="text-stone-400">... e mais {opsNaCategoria.length - 8}</li>
                                )}
                              </ul>
                            </div>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    );
                  })}
                </div>
              </TooltipProvider>
            </div>
          )}

          {/* Passo 2: Seleccionar Tipo de Lote */}
          {novoLotePasso === 2 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Button variant="ghost" size="sm" onClick={() => setNovoLotePasso(1)}>
                  ← Voltar
                </Button>
                <span className="text-stone-600">
                  Categoria: <strong>{categoriasFiltradas.find(c => c.id === novoLoteCategoria)?.nome}</strong>
                </span>
              </div>
              
              <p className="text-stone-600">Como pretende criar o lote?</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Lote Novo */}
                <button
                  onClick={() => {
                    setNovoLoteTipo('novo');
                    setNovoLotePasso(3);
                  }}
                  className="p-6 rounded-lg border-2 border-stone-200 hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  data-testid="btn-tipo-novo"
                >
                  <Plus className="h-8 w-8 text-blue-600 mb-2" />
                  <span className="block font-bold text-lg text-stone-800">Lote Novo</span>
                  <span className="block text-sm text-stone-500">Criar lote com peças ainda não alocadas</span>
                  <span className="block text-xs text-blue-600 mt-2">
                    Disponível: {(resumo.distribuicao_categorias?.[novoLoteCategoria]?.quantidade_restante ?? resumo.quantidade_total) || 0} peças
                  </span>
                </button>
                
                {/* União de Lotes */}
                <button
                  onClick={() => {
                    setNovoLoteTipo('uniao');
                    setNovoLotePasso(3);
                  }}
                  className="p-6 rounded-lg border-2 border-stone-200 hover:border-purple-500 hover:bg-purple-50 transition-all text-left"
                  data-testid="btn-tipo-uniao"
                >
                  <Merge className="h-8 w-8 text-purple-600 mb-2" />
                  <span className="block font-bold text-lg text-stone-800">União de Lotes</span>
                  <span className="block text-sm text-stone-500">Criar lote a partir de lotes existentes de outra categoria</span>
                  <span className="block text-xs text-purple-600 mt-2">
                    Avançar peças de outras categorias
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* Passo 3: Detalhes do Lote */}
          {novoLotePasso === 3 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Button variant="ghost" size="sm" onClick={() => setNovoLotePasso(2)}>
                  ← Voltar
                </Button>
                <span className="text-stone-600">
                  Categoria: <strong>{categoriasFiltradas.find(c => c.id === novoLoteCategoria)?.nome}</strong> |
                  Tipo: <strong>{novoLoteTipo === 'novo' ? 'Lote Novo' : 'União'}</strong>
                </span>
              </div>

              {/* Se for lote NOVO */}
              {novoLoteTipo === 'novo' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Quantidade de Peças *
                    </label>
                    {(() => {
                      const distribuicao = resumo.distribuicao_categorias || {};
                      const infoCat = distribuicao[novoLoteCategoria] || {};
                      const qtdRestante = infoCat.quantidade_restante !== undefined 
                        ? infoCat.quantidade_restante 
                        : (resumo.quantidade_total || quantidadeTotal);
                      return (
                        <>
                          <Input
                            type="number"
                            min="1"
                            max={qtdRestante}
                            value={novoLoteQuantidade}
                            onChange={(e) => setNovoLoteQuantidade(e.target.value)}
                            placeholder={`Máx: ${qtdRestante}`}
                            className="h-12"
                            data-testid="input-quantidade-novo"
                          />
                          <p className="text-xs text-stone-500 mt-1">
                            Disponível nesta categoria: <strong>{qtdRestante}</strong> de {resumo.quantidade_total || quantidadeTotal} peças
                          </p>
                        </>
                      );
                    })()}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Observações (opcional)
                    </label>
                    <Input
                      value={novoLoteObservacoes}
                      onChange={(e) => setNovoLoteObservacoes(e.target.value)}
                      placeholder="Ex: Lote inicial"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Nome do Lote (opcional)
                    </label>
                    <Input
                      value={novoLoteNome}
                      onChange={(e) => setNovoLoteNome(e.target.value)}
                      placeholder="Ex: Lote A"
                      className="h-12"
                      data-testid="input-nome-lote"
                    />
                  </div>
                </div>
              )}

              {/* Se for UNIÃO */}
              {novoLoteTipo === 'uniao' && (
                <div className="space-y-4">
                  {/* Seleccionar categoria de origem */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      1. Seleccione a categoria de origem dos lotes
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {categoriasFiltradas.filter(c => c.id !== novoLoteCategoria).map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => {
                            setCategoriaOrigemUniao(cat.id);
                            setLotesParaUniao([]);
                          }}
                          className={`px-4 py-2 rounded-lg border transition-all ${
                            categoriaOrigemUniao === cat.id
                              ? 'border-purple-500 bg-purple-100 text-purple-800'
                              : 'border-stone-200 hover:border-purple-300'
                          }`}
                        >
                          <span className="font-mono text-sm mr-1">{cat.codigo || '---'}</span>
                          {cat.nome}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Lista de lotes disponíveis */}
                  {categoriaOrigemUniao && (
                    <div>
                      <label className="block text-sm font-medium text-stone-700 mb-2">
                        2. Seleccione os lotes e defina as quantidades
                      </label>
                      <div className="border rounded-lg overflow-hidden">
                        {getLotesDisponiveis().length === 0 ? (
                          <div className="p-4 text-center text-stone-500">
                            Não existem lotes aprovados nesta categoria
                          </div>
                        ) : (
                          <div className="divide-y">
                            {getLotesDisponiveis().map(lote => {
                              const seleccionado = lotesParaUniao.find(l => l.loteId === lote.id);
                              return (
                                <div 
                                  key={lote.id} 
                                  className={`p-3 flex items-center gap-3 ${seleccionado ? 'bg-purple-50' : 'hover:bg-stone-50'}`}
                                >
                                  <Checkbox
                                    checked={!!seleccionado}
                                    onCheckedChange={() => toggleLoteUniao(lote)}
                                    data-testid={`check-lote-${lote.id}`}
                                  />
                                  <div className="flex-1">
                                    <span className="font-bold text-stone-800">
                                      {lote.codigo_lote || `L${lote.numero_lote}`}
                                    </span>
                                    {lote.status === 'pendente' && (
                                      <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                        Pendente
                                      </span>
                                    )}
                                    <span className="text-stone-500 ml-2">
                                      {lote.quantidade} peças disponíveis
                                    </span>
                                  </div>
                                  {seleccionado && (
                                    <div className="flex items-center gap-2">
                                      <Input
                                        type="number"
                                        min="1"
                                        max={lote.quantidade}
                                        value={seleccionado.quantidade}
                                        onChange={(e) => updateQuantidadeUniao(lote.id, e.target.value)}
                                        className="w-24 h-8"
                                      />
                                      <span className="text-xs text-stone-500">de {lote.quantidade}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      
                      {/* Total seleccionado */}
                      {lotesParaUniao.length > 0 && (
                        <div className="mt-3 p-3 bg-purple-100 rounded-lg flex items-center justify-between">
                          <span className="text-purple-800 font-medium">
                            Total: {getTotalPecasUniao()} peças de {lotesParaUniao.length} lote(s)
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Observações */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-1">
                      Observações (opcional)
                    </label>
                    <Input
                      value={novoLoteObservacoes}
                      onChange={(e) => setNovoLoteObservacoes(e.target.value)}
                      placeholder="Ex: União de lotes para acabamento"
                      className="h-12"
                    />
                  </div>
                </div>
              )}

              {/* Botões de acção */}
              <div className="flex gap-3 mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    onClose && onClose();
                    resetNovoLoteForm();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleCriarNovoLote}
                  disabled={saving || 
                    (novoLoteTipo === 'novo' && !novoLoteQuantidade) ||
                    (novoLoteTipo === 'uniao' && lotesParaUniao.length === 0)
                  }
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-confirmar-criar-lote"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Confirmar
                </Button>
              </div>
            </div>
          )}

          {/* PASSO 4 - CONFIRMAÇÃO */}
          {novoLotePasso === 4 && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="h-5 w-5 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-lg text-stone-800">Confirmar Criação do Lote</h3>
              </div>

              <div className="space-y-3 bg-stone-50 rounded-xl p-4">
                <h4 className="text-xs font-semibold uppercase text-stone-500 tracking-wide mb-3">Resumo</h4>

                <div className="flex items-center justify-between py-2 border-b border-stone-200 last:border-0">
                  <span className="text-sm text-stone-600">Categoria</span>
                  <span className="font-semibold text-navy">
                    {categoriasFiltradas.find(c => c.id === novoLoteCategoria)?.nome || novoLoteCategoria}
                  </span>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-stone-200 last:border-0">
                  <span className="text-sm text-stone-600">Tipo de Lote</span>
                  <span className={`font-semibold px-2 py-0.5 rounded text-xs ${
                    novoLoteTipo === 'novo' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                  }`}>
                    {novoLoteTipo === 'novo' ? 'Lote Novo' : 'União de Lotes'}
                  </span>
                </div>

                {novoLoteTipo === 'novo' && (
                  <div className="flex items-center justify-between py-2 border-b border-stone-200 last:border-0">
                    <span className="text-sm text-stone-600">Quantidade</span>
                    <span className="font-bold text-xl text-navy">{novoLoteQuantidade} <span className="text-sm font-normal text-stone-500">peças</span></span>
                  </div>
                )}

                {novoLoteTipo === 'uniao' && (
                  <div className="flex items-start justify-between py-2 border-b border-stone-200 last:border-0">
                    <span className="text-sm text-stone-600">Lotes a Unir</span>
                    <div className="text-right">
                      {lotesParaUniao.map(l => (
                        <div key={l.loteId} className="text-xs text-stone-700">
                          Lote {l.loteId?.slice(-4)} — {l.quantidade} peças
                        </div>
                      ))}
                      <div className="font-bold text-emerald-700 mt-1">
                        Total: {getTotalPecasUniao()} peças
                      </div>
                    </div>
                  </div>
                )}

                {novoLoteObservacoes && (
                  <div className="flex items-start justify-between py-2">
                    <span className="text-sm text-stone-600">Observações</span>
                    <span className="text-sm text-stone-700 italic text-right max-w-[60%]">{novoLoteObservacoes}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-3 mt-4 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setNovoLotePasso(3)}
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Voltar
                </Button>
                <Button
                  onClick={handleCriarNovoLote}
                  disabled={saving}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-confirmar-criar-lote"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Check className="h-4 w-4 mr-2" />
                  )}
                  Confirmar e Criar Lote
                </Button>
              </div>
            </div>
          )}
        </Card>
  );
}
